/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Alert, Box, Typography } from '@mui/material';
import { type ReactNode, useRef, useState, useEffect, useContext } from 'react';

import { useAppToIframeSynchronization } from '../../hooks/useAppToIframeSynchronization/useAppToIframeSynchronization';
import { useDimensions } from '../../hooks/useDimensions/useDimensions';
import { getElementsRatio } from '../../utils/getElementsRatio/getElementsRatio';
import { DisplayZoom } from './DisplayZoom/DisplayZoom';
import { Loader } from '../Loader/Loader';
import type { CKEditor5IframeProps } from './types';
import { BrowserFrame } from './BrowserFrame/BrowserFrame';
import { PADDING_BOTTOM } from '../../constants/mockBrowser';
import { createExtendedError } from '../../utils/createExtendedError/createExtendedError';
import { useErrorBoundary } from 'react-error-boundary';
import { EditorSettingsContext } from '../../contexts/EditorSettingsContext';

const MOCK_BROWSER_MIN_HEIGHT = 500;
const MOCK_BROWSER_PADDING_BOTTOM = 25;

export function CKEditor5Iframe( { previewLocation, locationOrigin, BuilderToggleButtons }: CKEditor5IframeProps ): ReactNode {
	const { toolbarOptions, editorPlugins, editorLanguage, editorType, preset } = useContext( EditorSettingsContext );
	// See #763.
	const MOBILE_EDITOR_HEIGHT = ( window?.screen?.height ?? 750 ) / 2;

	const { showBoundary } = useErrorBoundary();

	const [ shouldZoom, setShouldZoom ] = useState( true );
	const [ editorWidth, setEditorWidth ] = useState( 0 );
	const [ editorHeight, setEditorHeight ] = useState( 0 );
	const [ iframeContainerElement, setIframeContainerElement ] = useState<HTMLDivElement | null>( null );
	const iframeRef = useRef<HTMLIFrameElement | null>( null );
	const [ isIframeLoaded, setIsIframeLoaded ] = useState( false );
	const [ isFullscreen, setIsFullscreen ] = useState( false );
	const [ shouldZoomBeforeFullscreen, setShouldZoomBeforeFullscreen ] = useState( shouldZoom );
	const { width: iframeContainerWidth } = useDimensions( iframeContainerElement );
	const { height: windowHeight } = useDimensions( document.body );

	const [ scale, setScale ] = useState( 1 );

	useEffect( () => {
		setScale( getElementsRatio( iframeContainerWidth, editorWidth ) );
	}, [ iframeContainerWidth, editorWidth ] );

	const scaleByShouldZoom = shouldZoom ? scale : 1;
	const editorSizePercentageByScale = scaleByShouldZoom === 1 ? 100 : 100 / scaleByShouldZoom;
	const isMobile = iframeContainerWidth <= 767;

	useAppToIframeSynchronization( {
		toolbarOptions,
		editorType,
		editorLanguage,
		editorPlugins,
		preset: preset!,
		isIframeLoaded,
		iframeRef,
		locationOrigin,
		// Math.max() is needed to set min height of the preview. When max height is set in preview, the browser jumps too much.
		windowHeight: Math.max( ( windowHeight - PADDING_BOTTOM ) / scaleByShouldZoom, MOCK_BROWSER_MIN_HEIGHT ),
		onEditorDimensionsChange: ( { editorWidth, editorHeight } ) => {
			setEditorWidth( editorWidth );

			if ( isMobile ) {
				setEditorHeight( MOBILE_EDITOR_HEIGHT );
			} else {
				setEditorHeight( editorHeight );
			}
		},
		onCKEditorError: ( error, details ) => {
			const contextError = createExtendedError( error.message, details );
			showBoundary( contextError );
		},
		onFullscreenChange: handleOnFullscreenChange
	} );

	function handleOnFullscreenChange( isFullscreen: boolean ) {
		setIsFullscreen( isFullscreen );

		if ( isFullscreen ) {
			setShouldZoomBeforeFullscreen( shouldZoom );
			setShouldZoom( false );
		}

		if ( !isFullscreen ) {
			setShouldZoom( shouldZoomBeforeFullscreen );
		}
	}

	return (
		<div style={ { minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '18px' } }>
			<Alert
				sx={ {
					display: 'var(--low-resolution-warning)',
					'& .MuiAlert-icon > svg': {
						width: '16.77px' // This makes the <svg> exactly 14px.
					}
				} }
			>
				<Typography color='text.primary' fontWeight='400' fontSize='14px'>
					This resolution is too low to utilize all features. Please switch to a higher resolution for the best experience.
				</Typography>
			</Alert>
			<Box justifyContent={ BuilderToggleButtons ? 'space-between' : 'flex-end' } display={ 'flex' }>
				{ BuilderToggleButtons }
				<DisplayZoom
					shouldZoom={ shouldZoom }
					handleOnZoomClick={ setShouldZoom }
					isFullscreen={ isFullscreen }
				/>
			</Box>
			<div
				style={ {
					position: 'relative',
					minWidth: '300px',
					minHeight: MOCK_BROWSER_MIN_HEIGHT + 'px'
				} }
			>
				{ !isIframeLoaded && <Loader/> }

				<BrowserFrame
					frameViewHeight={
						isMobile ?
							MOBILE_EDITOR_HEIGHT :
							( editorHeight - MOCK_BROWSER_PADDING_BOTTOM ) * scaleByShouldZoom
					}
					setBrowserFrameRef={ setIframeContainerElement }
					style={ {
						opacity: isIframeLoaded ? '1' : '0',
						transition: 'opacity 0.1s ease-in-out 1s',
						width: '100%'
					} }
				>
					<iframe
						title={ 'editor-container-iframe' }
						ref={ iframeRef }
						src={ previewLocation }
						onLoad={ () => setIsIframeLoaded( true ) }
						data-cy={ 'ckeditor5-iframe' }
						style={ {
							border: 0,
							width: editorSizePercentageByScale + '%',
							height: editorSizePercentageByScale + '%',
							transform: `scale( ${ scaleByShouldZoom } )`,
							transformOrigin: 'left top',
							opacity: isIframeLoaded ? '1' : '0',
							transition: 'opacity 0.1s ease-in-out 1s',
							overflow: 'hidden',
							// position: 'absolute' is needed to prevent iframe from traveling up when typing.
							position: 'absolute',
							top: 0,
							left: 0
						} }
					>
					</iframe>
				</BrowserFrame>
			</div>
		</div>
	);
}
